import { weightBandInputSchema, type WeightBandInput } from '@chukua/common';
import { Alert, Button, Drawer, Form, Input, InputNumber } from 'antd';
import { useEffect } from 'react';
import type { CatalogueDrawerProps } from '../../../components/CataloguePanel';
import type { WeightBand } from '../../../reference-data.api';

type WeightBandFormValues = { code: string; name: string; minKg: number | null; maxKg: number | null };

const FIELD_MESSAGES: Record<string, string> = { maxKg: 'Max must be greater than min, or blank for no upper limit' };

export function WeightBandFormDrawer({ open, editing, error, submitting, onSubmit, onClose }: CatalogueDrawerProps<WeightBand, WeightBandInput>) {
  const [form] = Form.useForm<WeightBandFormValues>();

  useEffect(() => {
    if (open) form.setFieldsValue(editing ?? { code: '', name: '', minKg: 0, maxKg: null });
  }, [open, editing, form]);

  async function submit(values: WeightBandFormValues) {
    // The same check the server runs, so a bad range is a field error instead of a round trip.
    const parsed = weightBandInputSchema.safeParse({ ...values, maxKg: values.maxKg ?? null });
    if (!parsed.success) {
      form.setFields(
        parsed.error.issues.map((issue) => ({
          name: issue.path[0] as keyof WeightBandFormValues,
          errors: [FIELD_MESSAGES[String(issue.path[0])] ?? issue.message],
        })),
      );
      return;
    }
    if (await onSubmit(parsed.data)) onClose();
  }

  return (
    <Drawer open={open} onClose={onClose} title={editing ? 'Edit weight band' : 'New weight band'} placement="right" size={420} destroyOnClose>
      {error && <Alert className="mb-5" type="error" title={error} showIcon />}
      <Form<WeightBandFormValues> form={form} layout="vertical" requiredMark={false} onFinish={submit} disabled={submitting}>
        <Form.Item name="code" label="Code" rules={[{ required: true, message: 'Enter a code' }]}>
          <Input className="font-mono" autoFocus />
        </Form.Item>
        <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Enter a name' }]}>
          <Input />
        </Form.Item>
        <Form.Item name="minKg" label="Min kg" rules={[{ required: true, message: 'Enter the lowest weight in the band' }]}>
          <InputNumber className="w-full" min={0} precision={0} />
        </Form.Item>
        <Form.Item name="maxKg" label="Max kg" extra="Both ends are inclusive. Leave blank for no upper limit.">
          <InputNumber className="w-full" min={0} precision={0} />
        </Form.Item>
        <Form.Item className="mb-0 pt-2">
          <Button type="primary" htmlType="submit" loading={submitting}>
            {editing ? 'Save' : 'Create weight band'}
          </Button>
        </Form.Item>
      </Form>
    </Drawer>
  );
}
